import React from "react";
import { Link } from "react-router-dom";
import { logEvent } from "../utils/analytics";

interface ProjectCardProps {
  project: { id?: string; name: string; description: string; tech: string[]; slug?: string };
  onClick?: () => void;
  onElementVanish?: (element: string) => void;
  vanishingElements?: { title: boolean; description: boolean; tags: boolean; ghost: boolean };
}

export default function ProjectCard({ project, onClick, onElementVanish, vanishingElements }: ProjectCardProps) {
  const slug = project.slug || project.name.toLowerCase().replace(/\s/g, "-");
  const isProject404 = project.slug === "project-404";
  const projectId = project.id || slug || project.name;

  const trackOpen = () => {
    logEvent("project_open", { id: projectId });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isProject404 && onClick && e.target === e.currentTarget) {
      trackOpen();
      onClick();
      onElementVanish?.("ghost");
    }
  };

  const handleElementTap = (element: "title" | "description" | "tags") => {
    if (isProject404 && onElementVanish) {
      onElementVanish(element);
    }
  };

  const cardClasses = [
    "relative overflow-hidden border rounded-card p-6 transition duration-300 interactive-card",
    "bg-surface/80 text-text-muted focus-visible:outline-none focus-visible:shadow-focus h-full flex flex-col justify-between",
    isProject404
      ? "cursor-pointer border-red-500/50 hover:border-red-400 focus-visible:border-red-200"
      : "border-border-subtle hover:border-accent hover:shadow-glow"
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <article onClick={handleCardClick} className={cardClasses} tabIndex={isProject404 ? 0 : -1}>
      <div
        className={`absolute inset-x-0 top-0 h-px opacity-60 transition ${
          isProject404 ? "bg-gradient-to-r from-transparent via-red-400 to-transparent" : "bg-gradient-to-r from-transparent via-accent to-transparent"
        }`}
      />
      <div className="space-y-3 flex-1">
        <h4
          onClick={(e) => {
            e.stopPropagation();
            handleElementTap("title");
          }}
          className={`font-mono text-lg text-primary flex items-center gap-2 ${
            isProject404 ? "cursor-pointer hover:text-red-300" : ""
          } ${vanishingElements?.title ? "vanishing-fade" : ""}`}
        >
          {project.name}
        </h4>
        <p
          onClick={(e) => {
            e.stopPropagation();
            handleElementTap("description");
          }}
          className={`text-sm leading-relaxed ${
            isProject404 ? "cursor-pointer hover:text-red-300" : "text-text-muted"
          } ${vanishingElements?.description ? "vanishing-slide" : ""}`}
        >
          {project.description}
        </p>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        {project.tech.map((t) => (
          <span
            key={t}
            onClick={(e) => {
              e.stopPropagation();
              handleElementTap("tags");
            }}
            className={`text-[11px] font-mono uppercase tracking-wide px-3 py-1 rounded-pill border ${
              isProject404 ? "cursor-pointer hover:opacity-70 border-red-400/70" : "border-border-subtle/70"
            } ${vanishingElements?.tags ? "tag-vanishing" : ""}`}
          >
            {t}
          </span>
        ))}
      </div>
    </article>
  );

  if (isProject404 && onClick) {
    return <div className="h-full">{content}</div>;
  }

  return (
    <Link
      to={`/projects/${slug}`}
      className="block h-full focus-visible:outline-none focus-visible:ring-0"
      onClick={trackOpen}
    >
      {content}
    </Link>
  );
}

